import "@/types/DownloadManager/modelDownloaderBenchmark"; // Registers window.DownloadBenchmark
import { ErrorStack } from "@/types/errors/ErrorStack";
import { configureBlobStorageFromFeatures } from "@/types/storage/blobStorage";
import { AppSetting, AppSettingType, FeatureManagementType } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useAuthToken } from "@api/auth";
import axios from "@api/axiosInstance";
import AppSettingsDrawer from "@components/AppSettingsDrawer/AppSettingsDrawer";
import FullPageLoading from "@components/FullPageLoading";
import { LoadingHeader } from "@components/LoadingHeader";
import Notification from "@components/Notification/Notification";
import useAppStore from "@hooks/useAppStore";
import useMiniProfiler from "@hooks/useMiniProfiler";
import i18n from "@i18n/i18n";
import { Box } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import MainRoutes from "@routes/MainRoutes";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import "../../font.css";
import "./App.css";
import useServerEvents from "./hooks/useServerEvents";

const App = observer(() => {
  console.log("App");
  const appStore = useAppStore();
  const [openErrors, setOpenErrors] = useState(0);
  const [refreshTimeoutId, setRefreshTimeoutId] = useState<any | null>(null);
  const { notifyMessage, notifyOpen, setNotifyOpen } = useServerEvents();
  const authToken = useAuthToken();
  const appSettings = useAppSettings();
  useMiniProfiler({ enabled: !!appStore.applicationSettings });

  useEffect(() => {
    if (appSettings.data) {
      appStore.setAppSettings(appSettings.data);

      i18n.options.appSettings = Object.values(appSettings.data?.settings!).reduce((acc: Map<AppSettingType, any>, setting: AppSetting) => {
        acc.set(setting.key as AppSettingType, setting.value!);
        return acc;
      }, new Map<AppSettingType, any>());

      // Configure blob storage based on feature flags
      // Expects features like 'BlobStorageAzure' or 'BlobStorageS3'
      configureBlobStorageFromFeatures(appSettings.data.features ?? []);
    }
  }, [appSettings.data]);

  const enableMiniProfiler = useMemo(() => {
    return appStore.hasFeature(FeatureManagementType.MiniProfiler) ?? false;
  }, [appStore.applicationSettings]);

  useEffect(() => {
    console.log("authToken.data: ", authToken.data);

    if ((authToken.data?.token?.expiresIn || 0) > 0) {
      console.log("Setting refresh timeout: ", authToken.data?.token?.expiresIn);

      console.log("Clear refresh timeout: ", refreshTimeoutId);
      clearTimeout(refreshTimeoutId);

      // Schedule a token refresh a few seconds before the current token expires.
      const timeout = Math.max(1000, ((authToken.data?.token?.expiresIn || 0) - 300) * 1000);
      const timeoutId = setTimeout(async () => {
        console.log("Refreshing auth token");
        setRefreshTimeoutId(null);
        await authToken.refetch();
      }, timeout);

      console.log("Saving refresh timeout: ", timeoutId, timeout);
      setRefreshTimeoutId(timeoutId);
    }

    return () => {
      console.log("Clearing refresh timeout: ", refreshTimeoutId);
      clearTimeout(refreshTimeoutId);
    };
  }, [authToken.data]);

  useEffect(() => {
    if (openErrors > 0) {
      appStore.errorManager.setOpenErrors(true);
    }
  }, [openErrors]);

  useEffect(() => {
    axios.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        if (error?.response?.status !== 401) {
          ErrorStack.create({
            title: error?.response?.data?.title ?? i18n.t("app.error.title"),
            message: error?.response?.data?.detail ?? error?.response?.data?.Message ?? error?.response?.data?.Message ?? error?.message ?? i18n.t("app.error.unknown-axios-error"),
            severity: error?.response?.data?.severity,
            error,
          });
          setOpenErrors((val) => val + 1);
        }
        return Promise.reject(error);
      },
    );
  }, []);

  if (window.location.pathname === "/api/auth/logout") {
    clearTimeout(refreshTimeoutId);
    setRefreshTimeoutId(null);
  }

  // Delay rendering the app until the app settings have been loaded.
  // This ensures that the feature management settings are applied before any components are rendered.
  if (appSettings.isLoading) {
    return <FullPageLoading />;
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <AppSettingsDrawer />
      <Notification message={notifyMessage} open={notifyOpen} setOpen={setNotifyOpen} />
      <Layout loading={appStore.isPending}>
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: "100%", minHeight: "100%" }}>
          <MainRoutes />
        </Box>
      </Layout>
      {enableMiniProfiler && <Box id="mini-profiler" />}
    </>
  );
});

export const Layout = (props: any) => {
  return (
    <>
      <LoadingHeader loading={props.loading} />
      {props.children}
    </>
  );
};

export default App;
